// T3-TASK-ROLLUPS: the task-metric builder of New metric. A metric with `source = task` is
// counted from one board's tasks by the `task_metric.snapshot` job: "Count / sum / average of
// <number field> of tasks on <board>, by <the day they are done>, only <done | open | a status>".
// The builder writes the contract's `task_query`; the API checks it against the board again.
import { Field, FieldLabel, InfoTip, Input, NativeSelect, NativeSelectOption } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { MetricDef } from "../../api/types";
import { boardsAllQuery, fieldsQuery } from "../work/work-queries";

/** Past this many boards the picker offers a search. */
const SEARCH_FROM = 8;

export type TaskQuery = NonNullable<MetricDef["task_query"]>;
type Measure = TaskQuery["measure"];
type DateBasis = TaskQuery["date"];

export type TaskQueryForm = {
  board_id: string;
  measure: Measure;
  field_id: string;
  date: DateBasis;
  date_field_id: string;
  /** `any`, `done`, `open`, or `status:<key>`. */
  only: string;
  /**
   * The stored filter of a metric being edited (it may say more than `only` shows: a field, a
   * tag, several statuses); kept as it is while `only` stays `keptOnly` on the same board.
   */
  kept: TaskQuery["filter"] | null;
  keptOnly: string;
};

const NUMERIC = new Set(["number", "currency", "checkbox", "formula", "rollup"]);

export function formOfTaskQuery(q: TaskQuery | null | undefined): TaskQueryForm {
  const only = q?.filter?.statuses?.[0]
    ? `status:${q.filter.statuses[0]}`
    : q?.filter?.done === true
      ? "done"
      : q?.filter?.done === false
        ? "open"
        : "any";
  return {
    board_id: q?.board_id ?? "",
    measure: q?.measure ?? "count",
    field_id: q?.field_id ?? "",
    date: q?.date ?? "completed_at",
    date_field_id: q?.date_field_id ?? "",
    only,
    kept: q?.filter ?? null,
    keptOnly: only,
  };
}

/** The contract's `task_query`, or why the form is not one yet. */
export function taskQueryOfForm(f: TaskQueryForm): { query: TaskQuery } | { error: string } {
  if (!f.board_id) return { error: "Pick a board." };
  if (f.measure !== "count" && !f.field_id) return { error: "Pick the number to add up." };
  if (f.date === "field" && !f.date_field_id) return { error: "Pick the date field." };
  const filter: TaskQuery["filter"] =
    f.kept && f.only === f.keptOnly
      ? f.kept
      : f.only === "done"
        ? { done: true }
        : f.only === "open"
          ? { done: false }
          : f.only.startsWith("status:")
            ? { statuses: [f.only.slice(7)] }
            : {};
  return {
    query: {
      board_id: f.board_id,
      measure: f.measure,
      field_id: f.measure === "count" ? null : f.field_id,
      date: f.date,
      date_field_id: f.date === "field" ? f.date_field_id : null,
      filter,
    },
  };
}

/** The aggregation a task metric's measure implies (the API refuses any other). */
export const taskAggregation = (m: Measure): MetricDef["aggregation"] =>
  m === "avg" ? "average" : "cumulative";

export function TaskQueryBuilder({
  api,
  co,
  value,
  onChange,
  fixedBoard = false,
}: {
  api: Api;
  co: string;
  value: TaskQueryForm;
  onChange: (v: TaskQueryForm) => void;
  /** T8-BOARD-NUMBERS: the board is given (a board's own "Add number"): no board picker. */
  fixedBoard?: boolean;
}) {
  // W1-FIXES (T3 review): every board, not the first page, and a search when there are many
  const boards = useQuery(boardsAllQuery(api, co));
  const [find, setFind] = useState("");
  const fields = useQuery({ ...fieldsQuery(api, co, value.board_id), enabled: !!value.board_id });
  const allBoards = boards.data ?? [];
  const board = allBoards.find((b) => b.id === value.board_id);
  const needle = find.trim().toLowerCase();
  const shown = needle
    ? allBoards.filter((b) => b.id === value.board_id || b.name.toLowerCase().includes(needle))
    : allBoards;
  const all = fields.data?.data ?? [];
  const numbers = all.filter((f) => NUMERIC.has(f.type));
  const dates = all.filter((f) => f.type === "date");
  const set = <K extends keyof TaskQueryForm>(k: K, v: TaskQueryForm[K]) =>
    onChange({ ...value, [k]: v });
  const built = taskQueryOfForm(value);
  return (
    <fieldset className="flex flex-col gap-3 rounded-md border p-3" data-slot="task-query">
      <legend className="flex items-center gap-1 px-1 text-sm font-medium">
        {fixedBoard ? "Counted from this board" : "Counted from a board"}
        <InfoTip label="About task metrics">
          Recounted every 15 minutes from the board's tasks, per day in company time. A change to an
          older task recounts its day. The board's owner seat and the company get the number.
        </InfoTip>
      </legend>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field className={fixedBoard ? "hidden" : undefined}>
          <FieldLabel htmlFor="task-query-board">Board</FieldLabel>
          {allBoards.length > SEARCH_FROM ? (
            <Input
              type="search"
              aria-label="Search"
              placeholder="Search boards…"
              value={find}
              onChange={(e) => setFind(e.target.value)}
              data-slot="task-query-board-search"
            />
          ) : null}
          <NativeSelect
            id="task-query-board"
            value={value.board_id}
            onChange={(e) =>
              onChange({
                ...value,
                board_id: e.target.value,
                field_id: "",
                date_field_id: "",
                only: "any",
                // another board's fields and statuses: the stored filter no longer applies
                kept: null,
              })
            }
          >
            <NativeSelectOption value="">
              {boards.isPending
                ? "Loading boards…"
                : needle && shown.length === 0
                  ? "No board matches"
                  : "Pick a board"}
            </NativeSelectOption>
            {shown.map((b) => (
              <NativeSelectOption key={b.id} value={b.id}>
                {b.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
        <Field>
          <FieldLabel htmlFor="task-query-measure">Measure</FieldLabel>
          <NativeSelect
            id="task-query-measure"
            value={value.measure}
            onChange={(e) => set("measure", e.target.value as Measure)}
          >
            <NativeSelectOption value="count">Count of tasks</NativeSelectOption>
            <NativeSelectOption value="sum">Sum of a number</NativeSelectOption>
            <NativeSelectOption value="avg">Average of a number</NativeSelectOption>
          </NativeSelect>
        </Field>
        {value.measure !== "count" ? (
          <Field>
            <FieldLabel htmlFor="task-query-field">Number</FieldLabel>
            <NativeSelect
              id="task-query-field"
              value={value.field_id}
              disabled={!value.board_id}
              onChange={(e) => set("field_id", e.target.value)}
            >
              <NativeSelectOption value="">
                {numbers.length ? "Pick a field" : "No number field on the board"}
              </NativeSelectOption>
              {numbers.map((f) => (
                <NativeSelectOption key={f.id} value={f.id}>
                  {f.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
        ) : null}
        <Field>
          <FieldLabel htmlFor="task-query-date">Counts on</FieldLabel>
          <NativeSelect
            id="task-query-date"
            value={value.date}
            onChange={(e) => set("date", e.target.value as DateBasis)}
          >
            <NativeSelectOption value="completed_at">Day done</NativeSelectOption>
            <NativeSelectOption value="due_at">Due day</NativeSelectOption>
            <NativeSelectOption value="created_at">Day created</NativeSelectOption>
            {dates.length ? (
              <NativeSelectOption value="field">A date field</NativeSelectOption>
            ) : null}
          </NativeSelect>
        </Field>
        {value.date === "field" ? (
          <Field>
            <FieldLabel htmlFor="task-query-date-field">Date field</FieldLabel>
            <NativeSelect
              id="task-query-date-field"
              value={value.date_field_id}
              onChange={(e) => set("date_field_id", e.target.value)}
            >
              <NativeSelectOption value="">Pick a field</NativeSelectOption>
              {dates.map((f) => (
                <NativeSelectOption key={f.id} value={f.id}>
                  {f.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
        ) : null}
        <Field>
          <FieldLabel htmlFor="task-query-only">Only</FieldLabel>
          <NativeSelect
            id="task-query-only"
            value={value.only}
            onChange={(e) => set("only", e.target.value)}
          >
            <NativeSelectOption value="any">Every task</NativeSelectOption>
            <NativeSelectOption value="done">Done tasks</NativeSelectOption>
            <NativeSelectOption value="open">Open tasks</NativeSelectOption>
            {(board?.status_set ?? []).map((s) => (
              <NativeSelectOption key={s.key} value={`status:${s.key}`}>
                In {s.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
      </div>
      {"error" in built && value.board_id ? (
        <p className="text-xs text-danger-text" role="alert">
          {built.error}
        </p>
      ) : null}
    </fieldset>
  );
}
